import { useEffect, useRef } from 'react';
import { useI18n } from '../i18n.tsx';
import type { RaceSource, RaceWithVerification, SourceCheck } from '../types.ts';
import { StatusBadge } from './StatusBadge.tsx';

function CheckRow({ check, source }: { check: SourceCheck; source?: RaceSource }) {
  const { t } = useI18n();
  return (
    <div className="check">
      <div className="check-head">
        <span className="src">{source?.label ?? check.sourceId}</span>
        <StatusBadge status={check.status} />
      </div>

      <dl>
        {check.matchedAlias && (
          <>
            <dt>{t.matchedOn}</dt>
            <dd>“{check.matchedAlias}”</dd>
          </>
        )}
        <dt>{check.datesFound.length > 0 ? t.datesOnPage : t.noDatesOnPage}</dt>
        <dd>
          {check.datesFound.length > 0
            ? check.datesFound.map((d) => `${d.day}.${String(d.month).padStart(2, '0')}`).join(', ')
            : '—'}
        </dd>
        {check.error && (
          <>
            <dt>error</dt>
            <dd>{check.error}</dd>
          </>
        )}
      </dl>

      {check.snippet && <div className="snippet">{check.snippet}</div>}

      {source && (
        <div style={{ marginTop: 8 }}>
          <a href={source.url} target="_blank" rel="noreferrer noopener">
            {t.openSource} ↗
          </a>
        </div>
      )}
    </div>
  );
}

export function RaceDetail({
  race,
  sources,
  onClose,
}: {
  race: RaceWithVerification | null;
  sources: RaceSource[];
  onClose: () => void;
}) {
  const { t, lang, formatDate } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (race && !dialog.open) dialog.showModal();
    if (!race && dialog.open) dialog.close();
  }, [race]);

  if (!race) return <dialog className="detail" ref={ref} onClose={onClose} />;

  const status = race.verification?.status ?? 'unverified';
  const byId = new Map(sources.map((s) => [s.id, s]));

  return (
    <dialog className="detail" ref={ref} onClose={onClose} onCancel={onClose}>
      <div className="detail-head">
        <div>
          <h3>{race.name}</h3>
          <div className="race-where">
            {formatDate(race.startDate, race.endDate)}
            {race.datePrecision === 'month' && ` (${t.approxDate})`} · {race.location} ·{' '}
            {race.county}
          </div>
          <div style={{ marginTop: 8 }}>
            <StatusBadge status={status} />
          </div>
        </div>
        <button type="button" className="icon-button" onClick={onClose} aria-label={t.close}>
          ✕
        </button>
      </div>

      <div className="detail-body">
        {race.notes && <p className="note">{race.notes[lang]}</p>}

        <section className="detail-section">
          <h4>{t.distances}</h4>
          <ul className="distance-list">
            {race.distances.map((d, i) => (
              <li key={`${d.label}-${d.km}-${i}`} className={d.km === race.half.km ? 'is-half' : ''}>
                <span>{d.label}</span>
                <span className="num">
                  {d.km} km{d.elevationM ? ` · +${d.elevationM} m` : ''}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="detail-section">
          <h4>{t.evidence}</h4>
          <p className="race-where" style={{ marginBottom: 8 }}>
            {t.statusHelp[status]}
          </p>
          {race.verification ? (
            race.verification.checks.map((check) => (
              <CheckRow key={check.sourceId} check={check} source={byId.get(check.sourceId)} />
            ))
          ) : (
            <p className="race-where">{t.statusHelp.unverified}</p>
          )}
        </section>
      </div>
    </dialog>
  );
}
